/**
 * Window "error" events include failed resource loads (img, script, …).
 * Those should not replace the entire application with the fatal error page.
 */
export function isResourceLoadErrorEvent(event) {
    const { target } = event;
    if (!target || target === window || target === document) {
        return false;
    }
    if (typeof Element !== 'undefined' && target instanceof Element) {
        const tag = target.tagName;
        return ['IMG', 'SCRIPT', 'LINK', 'VIDEO', 'AUDIO', 'SOURCE', 'IFRAME', 'OBJECT', 'EMBED'].includes(tag);
    }
    return false;
}

/**
 * Cross-origin script throws are sanitized to "Script error." with target === window.
 */
export function isCrossOriginScriptErrorEvent(event) {
    const { target, message, lineno } = event;
    if (target && target !== window && target !== document) {
        return false;
    }
    const msg = typeof message === 'string' ? message.trim() : '';
    return msg === 'Script error.' && (lineno === 0 || lineno == null);
}

/** Chromium reports ResizeObserver loop warnings as window "error" events in some builds. */
export function isResizeObserverLoopErrorEvent(event) {
    const msg = typeof event.message === 'string' ? event.message : '';
    return /ResizeObserver loop/.test(msg);
}

/**
 * React 19+ throws when a rendered `href`/`src`/`action`/`formAction` resolves to a `javascript:` URL
 * (e.g. legacy `href="javascript:void(0)"` link patterns). It's a no-op security guard, not an app
 * failure, so it shouldn't blank the page — surface it in the console instead and keep fixing offending
 * markup at the source.
 */
export function isBlockedJavascriptUrlErrorEvent(event) {
    const eventMessage = typeof event.message === 'string' ? event.message : '';
    const errorMessage = typeof event.error?.message === 'string' ? event.error.message : '';
    return /React has blocked a javascript: URL/.test(eventMessage) || /React has blocked a javascript: URL/.test(errorMessage);
}

export function isBenignWindowErrorEvent(event) {
    return (
        isCrossOriginScriptErrorEvent(event) ||
        isResizeObserverLoopErrorEvent(event) ||
        isBlockedJavascriptUrlErrorEvent(event)
    );
}

export function isIgnorableWindowErrorEvent(event) {
    return isResourceLoadErrorEvent(event) || isBenignWindowErrorEvent(event);
}

export function isBenignUnhandledRejection(event) {
    const { reason } = event;
    if (reason == null) {
        return false;
    }
    if (reason.name === 'AbortError') {
        return true;
    }
    const message = typeof reason === 'string' ? reason : reason.message;
    return typeof message === 'string' && /ResizeObserver loop/.test(message);
}
